<script lang="ts">
	import { Button } from '$lib/components/ui/button';
	import { Slider } from '$lib/components/ui/slider';
	import {
		DropdownMenu,
		DropdownMenuContent,
		DropdownMenuLabel,
		DropdownMenuSeparator,
		DropdownMenuTrigger
	} from '$lib/components/ui/dropdown-menu';
	import { ChevronLeft, ChevronRight, Book } from 'lucide-svelte';
	import ReaderUiControls from './reader-ui-controls.svelte';
	import { goto, preloadData } from '$app/navigation';
	import { onMount } from 'svelte';

	let { currentPage, totalPages, storySlug } = $props<{
		currentPage: number;
		totalPages: number;
		storySlug: string;
	}>();

	let previousPageUrl = $derived(
		currentPage - 1 <= 0 ? `/story/${storySlug}` : `/story/${storySlug}/page/${currentPage - 1}`
	);
	let nextPageUrl = $derived(
		currentPage === totalPages
			? `/story/${storySlug}/page/end`
			: `/story/${storySlug}/page/${currentPage + 1}`
	);

	// Add keyboard navigation
	function handleKeydown(event: KeyboardEvent) {
		// Only handle arrow keys if no input elements are focused
		if (document.activeElement?.tagName === 'INPUT') return;

		if (event.key === 'ArrowRight') {
			event.preventDefault();
			goto(nextPageUrl);
		} else if (event.key === 'ArrowLeft') {
			event.preventDefault();
			goto(previousPageUrl);
		}
	}

	onMount(() => {
		// Add keyboard event listener
		window.addEventListener('keydown', handleKeydown);

		// Cleanup on component destruction
		return () => {
			window.removeEventListener('keydown', handleKeydown);
		};
	});

	// Speed up navigation by preloading the next page
	$effect(() => {
		preloadData(nextPageUrl);
	});

	function handleSliderChange(value: number[]) {
		currentPage = value[0];
	}

	function handlePageChange(value: number[]) {
		goto(`/story/${storySlug}/page/${currentPage}`);
	}

	// Add a derived value for progress percentage
	let progressPercentage = $derived((currentPage / totalPages) * 100);
</script>

<div
	class="relative flex w-full items-center justify-between rounded-full border border-border bg-background p-2 shadow-lg dark:bg-background/95 dark:backdrop-blur-sm"
>
	<Button href={previousPageUrl} variant="outline" size="icon" class="rounded-full">
		<ChevronLeft class="h-4 w-4" />
		<span class="sr-only">Previous page</span>
	</Button>

	<div class="mx-4 flex flex-1 items-center gap-2">
		<DropdownMenu>
			<DropdownMenuTrigger>
				<Button variant="ghost" class="rounded-full">
					<Book class="mr-2 h-4 w-4" />
					Page {currentPage}
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent class="w-56">
				<DropdownMenuLabel>Go to page</DropdownMenuLabel>
				<DropdownMenuSeparator />
				<div class="px-2 py-1.5">
					<Slider
						value={[currentPage]}
						min={1}
						max={totalPages}
						step={1}
						onValueChange={handleSliderChange}
						onValueCommit={handlePageChange}
						class="w-full"
					/>
				</div>
				<DropdownMenuLabel class="text-center text-xs">
					Page {currentPage} of {totalPages}
				</DropdownMenuLabel>
			</DropdownMenuContent>
		</DropdownMenu>

		<ReaderUiControls />
	</div>

	{#if currentPage === totalPages}
		<Button
			href={nextPageUrl}
			size="lg"
			class="group relative overflow-hidden rounded-full transition-all hover:shadow-lg hover:shadow-primary/25"
		>
			<div
				class="group-hover:animate-gradient absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10"
			></div>
			<span class="relative flex items-center">
				Finish
				<ChevronRight class="ml-2 h-6 w-6 transition-transform group-hover:translate-x-1" />
			</span>
		</Button>
	{:else}
		<Button
			href={nextPageUrl}
			size="lg"
			class="group relative overflow-hidden rounded-full transition-all hover:shadow-lg hover:shadow-primary/25"
		>
			<div
				class="group-hover:animate-gradient absolute inset-0 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10"
			></div>
			<span class="relative flex items-center">
				Next
				<ChevronRight class="ml-2 h-6 w-6 transition-transform group-hover:translate-x-1" />
			</span>
		</Button>
	{/if}

	<!-- Progress bar with adjusted positioning -->
	<div class="absolute inset-x-0 bottom-0 h-[2px] rounded-full px-6">
		<div
			class="h-full bg-primary/50 transition-all duration-300 ease-out"
			style:width="{progressPercentage}%"
		></div>
	</div>
</div>

<style lang="postcss">
	@keyframes gradient {
		0% {
			background-position: 0% 50%;
		}
		50% {
			background-position: 100% 50%;
		}
		100% {
			background-position: 0% 50%;
		}
	}

	:global(.animate-gradient) {
		animation: gradient 3s ease infinite;
		background-size: 200% 200%;
	}
</style>
